import Logger from '@/helpers/logger'
import { RegistrationDataContext } from '@/providers/registration-data-provider/registration-data-provider'
import { matchAfasCity } from '@/services/rest/addresses'
import { fetchChamberOfCommerceDetails, fetchChamberOfCommerceQuery } from '@/services/rest/companies'
import { CommerceDetailDTO, ReadCompanySearchResultApiResponse } from '@/types/__domain__/companies'

import { AxiosError } from 'axios'
import { createContext, ReactNode, use, useState, ReactElement } from 'react'

interface ChamberOfCommerceProviderProps {
    children: ReactNode
}

interface ChamberOfCommerceProviderValue {
    companyInfo: CommerceDetailDTO
    fetchQuery?: (searchTerm: string) => Promise<ReadCompanySearchResultApiResponse>
    setCompany?: (id: string) => Promise<boolean>
    loadingDetails: boolean
    clear?: () => void
    getMatchingAfasCity?: (countryCode: string, chamberOfCommerceCity: string) => Promise<string>
}

export const ChamberOfCommerceContext = createContext<ChamberOfCommerceProviderValue>({
    companyInfo: null,
    loadingDetails: false,
})

/** Provider that holds the selected company in the Chamber of Commerce lookup in state */
export const ChamberOfCommerceProvider = ({ children }: ChamberOfCommerceProviderProps): ReactElement => {
    const [companyInfo, setCompanyInfo] = useState<CommerceDetailDTO>(null)
    const [loadingDetails, setLoadingDetails] = useState<boolean>(false)
    const { clearRegistrationFormData } = use(RegistrationDataContext)

    const fetchQuery = (searchTerm: string) =>
        fetchChamberOfCommerceQuery(searchTerm).catch((error: AxiosError) => logError(error, null))

    /** @returns {boolean} true if data is retrieved */
    const setCompany = async (id: string): Promise<boolean> => {
        setLoadingDetails(true)
        return fetchChamberOfCommerceDetails(id)
            .then((company: CommerceDetailDTO) => {
                setCompanyInfo(company)
                return !!company
            })
            .catch((error: AxiosError) => logError(error, false))
            .finally(() => setLoadingDetails(false))
    }

    const getMatchingAfasCity = async (countryCode: string, chamberOfCommerceCity: string): Promise<string> => {
        try {
            const response = await matchAfasCity(countryCode, chamberOfCommerceCity)
            if (response?.isSuccess) {
                return response.data
            }
        } catch (e) {
            logError(e, e)
        }
    }

    function logError<T>(error: AxiosError, response: T): T {
        Logger.error(`Error fetching Chamber of Commerce data: ${error.message}`)
        return response
    }

    const clearCompanyInfo = (): void => {
        setCompanyInfo(null)
        clearRegistrationFormData()
    }

    const providerValue: ChamberOfCommerceProviderValue = {
        companyInfo,
        fetchQuery,
        setCompany,
        loadingDetails,
        clear: clearCompanyInfo,
        getMatchingAfasCity,
    }

    return <ChamberOfCommerceContext.Provider value={providerValue}>{children}</ChamberOfCommerceContext.Provider>
}
